import { cn } from "@/lib/utils";
import { Button } from "@/modules/shadcn/ui/button";
import { Textarea } from "@/modules/shadcn/ui/textarea";
import { formatElapsed, useElapsed } from "@/hooks/useElapsed";
import {
  VISION_FEEDBACK_MAX,
  VisionStreamPhaseEnum,
  VisionTranscriptTurn,
} from "@repo/schema";
import type { TFunction } from "i18next";
import { Bookmark, BookmarkCheck, Loader2, X } from "lucide-react";
import { KeyboardEvent, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PreAnnotateRunView, VisionSavedNote } from "../../types/preAnnotate";
import {
  BUTTON_APPROVE,
  BUTTON_CANCEL,
  BUTTON_CLOSE,
  BUTTON_EDIT_NOTE,
  BUTTON_GIVE_FEEDBACK,
  BUTTON_EXCLUDE_NOTE,
  BUTTON_RUN_AGAIN,
  BUTTON_SAVE_NOTE,
  BUTTON_SAVE_TO_NOTES,
  BUTTON_SEND_FEEDBACK,
  BUTTON_SETTINGS,
  FEEDBACK_HELP,
  FEEDBACK_LABEL,
  FEEDBACK_PLACEHOLDER,
  NOTE_EDIT_HELP,
  NOTE_EDIT_LABEL,
  NOTE_NOT_SAVED,
  NOTE_SAVED,
  RUNNING_TITLE,
  SLOW_RUN_AFTER_MS,
  STREAM_SLOW_NOTE,
  STREAM_THINKING,
  TRANSCRIPT_LOADING,
  TRANSCRIPT_OPENING_RUN,
  TRANSCRIPT_TITLE,
  TRANSCRIPT_UNAVAILABLE,
} from "./copy";
import { describeRunView, SettledRunView } from "./describeRunOutcome";

/** Only a stopped run fades on its own. */
const AUTO_DISMISS_MS = 8_000;

/**
 * A result never fades: the card is where the Robopipe Vision feedback turn
 * starts, and a conversation that vanished after eight seconds would take the
 * only way to correct it with it. Errors, a resumed conversation and a live run
 * wait too; only a cancelled run keeps the timer.
 */
const autoDismisses = (view: PreAnnotateRunView): boolean => view.kind === "cancelled";

interface PreAnnotateRunPanelProps {
  view: PreAnnotateRunView;
  /**
   * Close the panel. Receives the view it was showing so a stale timer can be
   * ignored.
   */
  onDismiss: (view: PreAnnotateRunView) => void;
  /** Stop the live run. Absent when the run cannot be cancelled. */
  onCancel?: () => void;
  onRunAgain: () => void;
  runAgainDisabledReason?: string;
  /**
   * Save the boxes on the canvas and close the card. Offered on a run that
   * landed boxes, and on a resumed conversation.
   */
  onApprove: () => void;
  /** Why Approve is greyed out (a save or another run is in flight). */
  approveDisabledReason?: string;
  /**
   * Continue the Robopipe Vision conversation with a correction. Present only
   * while a live session belongs to the outcome on screen — its absence is
   * what turns the feedback affordance off.
   */
  onSendFeedback?: (feedback: string) => void;
  /** Why Send is greyed out (another run is already in flight). */
  feedbackDisabledReason?: string;
  /**
   * What became of the last feedback turn's project note: saved by the model
   * (its author is the labeller looking at it, so it is always editable and
   * excludable) or kept out, with a Save to override. Null when there is
   * nothing to say.
   */
  savedNote?: VisionSavedNote | null;
  /** Why the last Edit, Exclude or Save did not go through, if it did not. */
  noteError?: string | null;
  /** A note request is in flight: the chips wait rather than double-send. */
  noteBusy?: boolean;
  onRemoveNote?: (noteId: number) => void;
  /** Save a feedback turn the model kept out of the notes (or an excluded one). */
  onSaveNote?: (turn: number) => void;
  /**
   * Overwrite the saved note's text. Resolves whether the server took it; the
   * card keeps its editor open on false so the draft is not lost.
   */
  onUpdateNote?: (noteId: number, text: string) => Promise<boolean>;
  onOpenSettings: () => void;
  /**
   * The turns this conversation already has, or null when the labeler has not
   * asked to see them.
   *
   * Null is "not open", not "empty". A resumed conversation is the one case
   * where the card has to say what was already tried — somebody returning to
   * an image days later has no other way to know, and would otherwise repeat a
   * correction the model has already been given.
   */
  transcript?: VisionTranscriptTurn[] | null;
  transcriptLoading?: boolean;
}

/**
 * The auto-annotation run, floating above the labels bar in the canvas column.
 *
 * A compact spinner card while a run is live, a result card once it settles.
 * Replaces the bottom-right toasts: the labeler is looking at the image, so
 * the answer belongs next to it, and a long run needs a Cancel that stays in
 * view.
 */
export const PreAnnotateRunPanel = ({
  view,
  ...handlers
}: PreAnnotateRunPanelProps) => (
  <div
    data-slot="pre-annotate-run-panel"
    className="pointer-events-none absolute inset-x-2 bottom-[70px] z-10 flex justify-center"
  >
    {/* Keyed on the kind: a fresh card animates in when the run settles, a
        progress tick or a hover state does not, and the hover flag resets
        with the card it belonged to. An outcome → running → outcome round
        trip therefore also clears a half-typed feedback draft. */}
    <RunCard key={view.kind} view={view} {...handlers} />
  </div>
);

const RunCard = ({ view, onDismiss, ...handlers }: PreAnnotateRunPanelProps) => {
  const [hovered, setHovered] = useState(false);

  // Timer keyed on the view object: every transition replaces it, which
  // clears the previous countdown; hovering pauses, leaving restarts in full.
  useEffect(() => {
    if (!autoDismisses(view) || hovered) return;
    const timer = setTimeout(() => onDismiss(view), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [view, hovered, onDismiss]);

  return (
    <div
      className="pointer-events-auto flex max-w-full justify-center animate-in fade-in slide-in-from-bottom-2 duration-200"
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      {view.kind === "running" ? (
        <RunningCard view={view} onCancel={handlers.onCancel} />
      ) : (
        <ResultCard view={view} onDismiss={onDismiss} {...handlers} />
      )}
    </div>
  );
};

// ── Live run ─────────────────────────────────────────────────────────────────

interface RunningCardProps {
  view: Extract<PreAnnotateRunView, { kind: "running" }>;
  onCancel?: () => void;
}

/**
 * The progress line under the title.
 *
 * The model's own words win whenever there are any: the heading of its current
 * thought section ("Distinguishing Salami Types") says what it is doing with
 * THIS image in three or four words, which is the difference between waiting
 * and wondering — the prose under each heading is too long for a spinner.
 * Once boxes start arriving the count takes over, because by then the
 * interesting fact is how many.
 *
 * Nothing is said during `image` — downloading and downscaling the frame. It
 * is the short phase (a few seconds), the elapsed clock is already running,
 * and a line that appeared only to be replaced would read as a glitch.
 */
const progressLine = (
  view: Extract<PreAnnotateRunView, { kind: "running" }>,
  t: TFunction<["label", "common"]>,
): string | null => {
  if (view.detail) return view.detail;
  switch (view.phase) {
    case VisionStreamPhaseEnum.BOXES:
      return t("label:runPanel.streamBoxes", { count: view.boxCount ?? 0 });
    case VisionStreamPhaseEnum.THINKING:
      return view.thought?.trim() || t(STREAM_THINKING);
    default:
      return null;
  }
};

const RunningCard = ({ view, onCancel }: RunningCardProps) => {
  const { t } = useTranslation(["label", "common"]);
  const cancellable = onCancel !== undefined;
  const elapsed = useElapsed(view.startedAt);
  const line = progressLine(view, t);
  return (
    <div
      role="status"
      className="flex max-w-[520px] items-center gap-3.5 rounded-[14px] bg-white/[0.98] py-3.5 pl-5 pr-3 shadow-[0_4px_16px_rgba(0,0,0,0.12),inset_0_0_0_1px_rgba(0,0,0,0.08)]"
    >
      <Loader2 className="size-[18px] shrink-0 animate-spin text-primary" aria-hidden />
      <div className="flex min-w-0 flex-col">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium">{t(RUNNING_TITLE[view.modelType])}</span>
          {/* Tabular figures so the card does not twitch every second. */}
          <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
            {formatElapsed(elapsed)}
          </span>
        </div>
        {line && (
          // One line, truncated: a heading is short, but the model writes them,
          // and a card that grew under the cursor would move Cancel away.
          <span className="truncate text-xs text-muted-foreground">{line}</span>
        )}
        {elapsed >= SLOW_RUN_AFTER_MS && (
          <span className="mt-0.5 text-[11px] text-muted-foreground/70">
            {t(STREAM_SLOW_NOTE)}
          </span>
        )}
      </div>
      {cancellable && (
        <Button
          variant="ghost"
          size="sm"
          className="ml-2 shrink-0"
          onClick={onCancel}
        >
          {t(BUTTON_CANCEL)}
        </Button>
      )}
    </div>
  );
};

// ── A resumed conversation's ledger ──────────────────────────────────────────

/**
 * Every turn this conversation has had, oldest first.
 *
 * Read-only and capped in height: it is context for the correction the labeler
 * is about to write, not the main event, and a twenty-turn thread must not
 * push the feedback box off the bottom of the canvas.
 */
const Transcript = ({
  turns,
  loading,
}: {
  turns: VisionTranscriptTurn[];
  loading: boolean;
}) => {
  const { t } = useTranslation(["label", "common"]);
  return (
    <div className="flex flex-col gap-1.5 px-6 pt-3">
      <p className="text-sm font-medium">{t(TRANSCRIPT_TITLE)}</p>
      {loading && turns.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t(TRANSCRIPT_LOADING)}</p>
      ) : turns.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t(TRANSCRIPT_UNAVAILABLE)}</p>
      ) : (
        <ol className="flex max-h-40 flex-col gap-2 overflow-y-auto rounded-md bg-black/[0.03] px-3 py-2.5">
          {turns.map((turn) => (
            <li key={turn.index} className="flex flex-col gap-0.5">
              <span className="text-xs font-medium">
                {turn.index}. {turn.feedback ?? t(TRANSCRIPT_OPENING_RUN)}
              </span>
              <span className="text-xs text-muted-foreground">
                {t("runPanel.transcript.boxes", { count: turn.boxCount })}
                {turn.summary ? ` - ${turn.summary}` : ""}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

// ── Settled run ──────────────────────────────────────────────────────────────

interface ResultCardProps extends Omit<PreAnnotateRunPanelProps, "view"> {
  view: SettledRunView;
}

const ResultCard = ({
  view,
  onDismiss,
  transcript = null,
  transcriptLoading = false,
  onRunAgain,
  runAgainDisabledReason,
  onApprove,
  approveDisabledReason,
  onSendFeedback,
  feedbackDisabledReason,
  savedNote,
  noteError,
  noteBusy,
  onRemoveNote,
  onSaveNote,
  onUpdateNote,
  onOpenSettings,
}: ResultCardProps) => {
  const { t } = useTranslation(["label", "common"]);
  const { icon: Icon, tone, title, body, summary, role } = describeRunView(view, t);
  const note = view.kind === "outcome" ? view.note : undefined;

  // A resumed conversation opens with the box already showing: the labeler
  // clicked Feedback to get here, so asking them to click it again would be
  // one step of ceremony for nothing.
  const [feedbackOpen, setFeedbackOpen] = useState(transcript !== null);
  const [feedbackText, setFeedbackText] = useState("");
  // The saved note's editor: null while closed, else the draft.
  const [noteDraft, setNoteDraft] = useState<string | null>(null);
  const [noteSaving, setNoteSaving] = useState(false);
  const noteSaveBlocked =
    noteSaving ||
    noteDraft === null ||
    noteDraft.trim().length === 0 ||
    noteDraft.trim() === savedNote?.text;

  const handleSaveNote = async () => {
    if (noteSaveBlocked || !savedNote || savedNote.noteId === null || !onUpdateNote)
      return;
    setNoteSaving(true);
    const saved = await onUpdateNote(savedNote.noteId, noteDraft!.trim());
    setNoteSaving(false);
    if (saved) setNoteDraft(null);
  };

  // A correction only makes sense while the conversation that produced these
  // boxes is still open, which is exactly when the page hands us a sender. A
  // failed run has no boxes to correct, so it offers Run again instead.
  const canGiveFeedback =
    onSendFeedback !== undefined &&
    (view.kind === "resumed" ||
      (view.kind === "outcome" && view.outcome.kind !== "error"));
  const canRunAgain =
    view.kind === "outcome" && view.outcome.kind !== "done" && !canGiveFeedback;
  // Only when there are boxes to approve: a run that found nothing or failed
  // has nothing to commit.
  const canApprove =
    view.kind === "resumed" ||
    (view.kind === "outcome" && view.outcome.kind === "done");
  const sendBlocked =
    feedbackText.trim().length === 0 || feedbackDisabledReason !== undefined;

  const handleSend = () => {
    if (sendBlocked) return;
    onSendFeedback?.(feedbackText.trim());
  };

  // Escape backs out one step at a time: the drafts first, the card second.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape") return;
    event.stopPropagation();
    if (noteDraft !== null) {
      setNoteDraft(null);
      return;
    }
    if (feedbackOpen) {
      setFeedbackOpen(false);
      return;
    }
    onDismiss(view);
  };

  return (
    <div
      role={role}
      onKeyDown={handleKeyDown}
      className="flex w-[min(560px,100%)] flex-col overflow-hidden rounded-[14px] bg-white shadow-[0_10px_32px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.08)]"
    >
      <div className="flex items-start justify-between gap-4 px-6 pt-[18px]">
        <div className="flex min-w-0 items-center gap-2.5">
          <Icon className={cn("size-5 shrink-0", tone)} aria-hidden />
          <h3 className="text-xl font-semibold leading-[26px] tracking-[-0.01em]">
            {title}
          </h3>
        </div>
        <Button
          variant="ghost"
          size="icon-xs"
          className="-mr-2 -mt-1 shrink-0 text-muted-foreground"
          aria-label={t(BUTTON_CLOSE)}
          onClick={() => onDismiss(view)}
        >
          <X />
        </Button>
      </div>
      {(body || summary || note) && (
        <div className="flex flex-col gap-1 px-6 pt-2.5">
          {body && <p className="text-sm text-muted-foreground">{body}</p>}
          {summary && (
            <p className="text-xs text-muted-foreground/80">{summary}</p>
          )}
          {note && <p className="text-xs text-muted-foreground/80">{note}</p>}
        </div>
      )}
      {savedNote && (
        <div className="px-6 pt-3">
          <NoteChip
            saved={savedNote.noteId !== null}
            busy={noteBusy}
            onEdit={
              onUpdateNote && noteDraft === null
                ? () => setNoteDraft(savedNote.text)
                : undefined
            }
            onSave={onSaveNote ? () => onSaveNote(savedNote.turn) : undefined}
            onRemove={
              onRemoveNote && savedNote.noteId !== null
                ? () => onRemoveNote(savedNote.noteId!)
                : undefined
            }
          />
        </div>
      )}
      {savedNote && savedNote.noteId !== null && noteDraft !== null && (
        <div className="flex flex-col gap-1.5 px-6 pt-2">
          <label htmlFor="pre-annotate-note-edit" className="sr-only">
            {t(NOTE_EDIT_LABEL)}
          </label>
          <Textarea
            id="pre-annotate-note-edit"
            rows={3}
            maxLength={VISION_FEEDBACK_MAX}
            autoFocus
            value={noteDraft}
            onChange={(event) => setNoteDraft(event.target.value)}
          />
          <p className="text-xs text-muted-foreground">{t(NOTE_EDIT_HELP)}</p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={noteSaving}
              onClick={() => setNoteDraft(null)}
            >
              {t(BUTTON_CANCEL)}
            </Button>
            <Button size="sm" disabled={noteSaveBlocked} onClick={handleSaveNote}>
              {noteSaving && <Loader2 className="animate-spin" aria-hidden />}
              {t(BUTTON_SAVE_NOTE)}
            </Button>
          </div>
        </div>
      )}
      {noteError && <p className="px-6 pt-2 text-xs text-destructive">{noteError}</p>}
      {transcript !== null && (
        <Transcript
          turns={transcript}
          loading={transcriptLoading}
        />
      )}
      {feedbackOpen && (
        <div className="flex flex-col gap-1.5 px-6 pt-3">
          <label
            htmlFor="pre-annotate-feedback"
            className="text-sm font-medium"
          >
            {t(FEEDBACK_LABEL)}
          </label>
          <Textarea
            id="pre-annotate-feedback"
            rows={3}
            maxLength={VISION_FEEDBACK_MAX}
            autoFocus
            value={feedbackText}
            placeholder={t(FEEDBACK_PLACEHOLDER)}
            onChange={(event) => setFeedbackText(event.target.value)}
          />
          <p className="text-xs text-muted-foreground">{t(FEEDBACK_HELP)}</p>
        </div>
      )}
      <div className="mt-4 flex items-center justify-between gap-3 border-t border-black/[0.08] px-6 pb-[18px] pt-3.5">
        <Button
          variant="ghost"
          size="lg"
          className="-ml-3 text-emerald-600 hover:text-emerald-700"
          onClick={onOpenSettings}
        >
          {t(BUTTON_SETTINGS)}
        </Button>
        <div className="flex shrink-0 gap-3">
          {canGiveFeedback &&
            (feedbackOpen ? (
              <>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setFeedbackOpen(false)}
                >
                  {t(BUTTON_CANCEL)}
                </Button>
                <WithReason reason={feedbackDisabledReason}>
                  <Button size="lg" disabled={sendBlocked} onClick={handleSend}>
                    {t(BUTTON_SEND_FEEDBACK)}
                  </Button>
                </WithReason>
              </>
            ) : (
              <Button
                variant={canApprove ? "outline" : "default"}
                size="lg"
                onClick={() => setFeedbackOpen(true)}
              >
                {t(BUTTON_GIVE_FEEDBACK)}
              </Button>
            ))}
          {canApprove && !feedbackOpen && (
            <WithReason reason={approveDisabledReason}>
              <Button
                size="lg"
                disabled={approveDisabledReason !== undefined}
                onClick={onApprove}
              >
                {t(BUTTON_APPROVE)}
              </Button>
            </WithReason>
          )}
          {canRunAgain && (
            <WithReason reason={runAgainDisabledReason}>
              <Button
                size="lg"
                disabled={runAgainDisabledReason !== undefined}
                onClick={onRunAgain}
              >
                {t(BUTTON_RUN_AGAIN)}
              </Button>
            </WithReason>
          )}
        </div>
      </div>
    </div>
  );
};

// ── The project note a feedback turn became ──────────────────────────────────

/**
 * "Saved to project notes · Edit · Exclude" or "Not saved to project notes ·
 * Save". The model decides whether a feedback turn is a rule for the whole
 * project; a saved note goes to every later run, so the card says so and
 * offers a way out for a correction that only made sense here, and a way in
 * for one the model kept out that the labeller knows applies everywhere.
 *
 * `onRemove` absent means there is nothing to exclude, `onSave` absent means
 * no way to save, and `onEdit` absent means the note is not editable here.
 */
const NoteChip = ({
  saved,
  busy = false,
  onEdit,
  onSave,
  onRemove,
}: {
  saved: boolean;
  busy?: boolean;
  onEdit?: () => void;
  onSave?: () => void;
  onRemove?: () => void;
}) => {
  const { t } = useTranslation(["label", "common"]);
  const Icon = saved ? BookmarkCheck : Bookmark;
  const action = (label: string, onClick: () => void) => (
    <>
      <span aria-hidden>·</span>
      <button
        type="button"
        className="font-medium underline-offset-2 hover:underline disabled:pointer-events-none disabled:opacity-50"
        disabled={busy}
        onClick={onClick}
      >
        {label}
      </button>
    </>
  );
  return (
    <span
      className={cn(
        "flex items-center gap-1.5 text-xs",
        saved ? "text-emerald-700" : "text-muted-foreground",
      )}
    >
      <Icon className="size-3.5 shrink-0" aria-hidden />
      {saved ? t(NOTE_SAVED) : t(NOTE_NOT_SAVED)}
      {saved && onEdit && action(t(BUTTON_EDIT_NOTE), onEdit)}
      {saved && onRemove && action(t(BUTTON_EXCLUDE_NOTE), onRemove)}
      {!saved && onSave && action(t(BUTTON_SAVE_TO_NOTES), onSave)}
    </span>
  );
};

/**
 * A disabled button swallows pointer events, so the reason goes on a wrapper.
 * Same native-title approach as the toolbar's ⚙ button.
 */
const WithReason = ({
  reason,
  children,
}: {
  reason?: string;
  children: ReactNode;
}) =>
  reason ? (
    <span title={reason} className="inline-flex">
      {children}
    </span>
  ) : (
    <>{children}</>
  );
